import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Styled from 'styled-components';
import { Helmet } from 'react-helmet';
import Table from 'semantic-table-grid';
import { Header, Icon, Pagination, Confirm, Modal, Label } from 'semantic-ui-react';
import _ from 'lodash';
import moment from 'moment';

// Components
import { BtnWithActions } from 'components/Buttons';
import FilterBar from 'components/RecessoList/FilterBar';
import EmptyMessage from 'components/Forms/UI/EmptyMessage';
import UpdateStateRecesso from 'components/RecessoList/UpdateStateRecesso';

// Services
import RecessoService from 'shared/services/api/recesso/index';
import aclService from 'shared/lib/acl';
import { ACL, ROUTES } from 'shared/constants';
import { RECESSO_STATE } from './constantRecessi';

const TableOverflow = Styled.div`
  display: block;
  margin: -14px -14px -10px;
  overflow-x: auto;
  padding: 14px;
  & td {
    position: relative;
  }
`;

const CounterLabel = Styled(Label)`
  padding-bottom: 1em !important;
  padding-top: 1em !important;
  font-size: 15px !important;
`;

class RecessiList extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isLoading: false, // Manage loading of table
      page: 1, // Manage current page for pagination of table of table
      totalPages: 1, // Manage total pages for pagination of table
      recessi: [], // Recessi ti view
      lastFilters: {}, // Manage filter
      orderField: 'receiptDate', // Manage order params for table
      orderDirection: 'desc', // Manage order params for table
      currentRecesso: null, // Current data of recesso
      removeRecessoConfirm: false, // Modal to remove recesso
      updateState: false, // Modal to update state
      totalRecessi: null, // Total of recessi
    };
  }

  componentDidMount() {
    this.loadData();
    this.loadTotalRecessi();
  }

  /** Define action for each recesso */
  getActions = (recesso) =>
    [
      {
        icon: 'edit',
        label: 'Modifica',
        action: () => this.props.history.push(`${ROUTES.GESTIONE_RECESSI}/${recesso.id}`),
      },
      aclService.check(this.props.auth, ACL.CAN_UPDATE_STATE_RECESSO) && {
        icon: 'exchange',
        label: 'Aggiorna stato',
        action: () => this.setState({ updateState: true, currentRecesso: recesso }),
      },
      aclService.check(this.props.auth, ACL.CAN_REMOVE_RECESSO) && {
        icon: 'delete',
        label: 'Elimina',
        action: () => this.setState({ removeRecessoConfirm: true, currentRecesso: recesso }),
      },
    ].filter(Boolean);

  /** Check direction of menu */
  isMenuUpward = (recesso) => this.state.recessi.length - _.findLastIndex(this.state.recessi, recesso) < 3;

  /** Define columns for table */
  columns = [
    {
      key: 'collaboratore',
      name: 'Collaboratore',
      formatter: ({ data }) => (
        <div>
          <span style={{ whiteSpace: 'nowrap', display: 'block' }}>
            {_.get(data, 'collaboratore.nominativo') || null}
          </span>
          <span style={{ whiteSpace: 'nowrap', display: 'block' }}>
            {_.get(data, 'collaboratore.codiceFiscale') || null}
          </span>
        </div>
      ),
      sortable: false,
    },
    {
      key: 'posizione',
      name: 'Posizione',
      formatter: ({ data }) => (
        <div>
          <span style={{ whiteSpace: 'nowrap', display: 'block' }}>
            {_.get(data, 'collaboratore.responsabileArea') || null}
          </span>
          <span style={{ whiteSpace: 'nowrap', display: 'block' }}>
            {_.get(data, 'collaboratore.seniorManager') || null}
          </span>
          <span style={{ whiteSpace: 'nowrap', display: 'block' }}>{_.get(data, 'collaboratore.agenzia') || null}</span>
          <span style={{ whiteSpace: 'nowrap', display: 'block' }}>
            {_.get(data, 'collaboratore.contratto') || null}
          </span>
        </div>
      ),
      sortable: false,
    },
    {
      key: 'dataContratto',
      name: 'Data contratto',
      formatter: ({ data }) => (_.has(data, 'collaboratore.dataDecorrenza') ? data.collaboratore.dataDecorrenza : null),
      sortable: false,
    },
    {
      key: 'origin',
      name: 'Origine / Macro motivazione',
      formatter: ({ data }) => (
        <div style={{ whiteSpace: 'nowrap' }}>
          {_.has(data, '_embedded.origin') ? data._embedded.origin.description : null} <br />
          {_.has(data, '_embedded.macroReason') ? data._embedded.macroReason.description : null}
        </div>
      ),
      sortable: false,
    },
    {
      key: 'receiptDate',
      name: 'Data ricezione',
      formatter: ({ data }) => (_.get(data, 'receiptDate') ? moment.unix(data.receiptDate).format('DD-MM-YYYY') : null),
      sortable: true,
    },
    {
      key: 'stato',
      name: 'Stato',
      formatter: ({ data }) => RECESSO_STATE[data.state],
      sortable: false,
    },
    {
      key: 'expectedDate',
      name: 'Data prevista recesso',
      formatter: ({ data }) =>
        _.get(data, 'expectedDate') ? moment.unix(data.expectedDate).format('DD-MM-YYYY') : null,
      sortable: true,
    },
    {
      key: 'dataRecesso',
      name: 'Data recesso',
      formatter: ({ data }) =>
        _.get(data, 'withdrawalDate') ? moment.unix(data.withdrawalDate).format('DD-MM-YYYY') : null,
      sortable: false,
    },
    {
      key: 'rinuciaPreavviso',
      name: 'Rinuncia preavviso',
      formatter: ({ data }) =>
        data.waiverNotice ? (
          <div style={{ textAlign: 'center' }}>
            <Icon name="check" color="green" />
          </div>
        ) : (
          <div style={{ textAlign: 'center' }}>
            <Icon name="x" color="red" />
          </div>
        ),
      sortable: false,
    },
    {
      key: 'intervistaEffettuata',
      name: 'Intervista Effetuata',
      formatter: ({ data }) =>
        data.interview ? (
          <div style={{ textAlign: 'center' }}>
            <Icon name="check" color="green" />
          </div>
        ) : (
          <div style={{ textAlign: 'center' }}>
            <Icon name="x" color="red" />
          </div>
        ),
      sortable: false,
    },
    {
      key: 'pattoConcorrenza',
      name: 'Patto di non Concorrenza',
      formatter: ({ data }) =>
        data.nonCompete ? (
          <div style={{ textAlign: 'center' }}>
            <Icon name="check" color="green" />
          </div>
        ) : (
          <div style={{ textAlign: 'center' }}>
            <Icon name="x" color="red" />
          </div>
        ),
      sortable: false,
    },
    // {
    //   key: 'alert',
    //   name: 'Alert',
    //   formatter: ({ data }) => data.alert || null,
    //   sortable: false,
    // },
    {
      key: 'reasonModuleRA',
      name: 'Motivazione',
      formatter: ({ data }) => data.reasonModuleRA && data.reasonModuleRA.trim() !== '' ? (
        <div style={{ textAlign: 'center' }}>
          <Icon name="check" color="green" />
        </div>
        ) : (
          <div style={{ textAlign: 'center' }}>
            <Icon name="x" color="red" />
          </div>
        ),
      sortable: true,
    },
    {
      key: 'action',
      name: 'Azioni',
      formatter: ({ data }) => (
        <div style={{ textAlign: 'center' }}>
          <BtnWithActions
            key="other-actions"
            actions={this.getActions(data)}
            otherActions={[]}
            upward={this.isMenuUpward(data)}
          />
        </div>
      ),
      width: 'auto',
    },
  ].filter(Boolean);

  loadTotalRecessi = () => {
    RecessoService.getTotalRecessi()
      .then((res) => {
        this.setState({ totalRecessi: res.totalRecesso });
      })
      .catch((err) =>
        this.props.notification().addNotification({
          message: _.get(err, 'data.detail') || 'Errore nel recupero del totale dei recessi',
          level: 'error',
        })
      );
  };

  /** Load all recessi */
  loadData = (filters, isFiltering) => {
    this.setState({ isLoading: true });

    let currentPage = this.state.page;

    if (isFiltering !== false) {
      currentPage = 1;
      this.setState({ page: 1 });
    }

    if (!_.isEmpty(filters)) {
      this.setState({ lastFilters: filters });
    }

    if (currentPage === 0) {
      currentPage = 1;
    }

    RecessoService.getAll(currentPage, filters || { active: true }, this.state.orderField, this.state.orderDirection)
      .then((res) => {
        this.setState({ totalPages: res.page_count, recessi: res._embedded.recesso });
      })
      .catch((err) =>
        this.props.notification().addNotification({
          message: _.get(err, 'data.detail') || 'Errore nel recupero dei dati',
          level: 'error',
        })
      )
      .finally(() => this.setState({ isLoading: false }));
  };

  /** Redirect to form to add new recesso */
  addRecesso = () => this.props.history.push(`${ROUTES.GESTIONE_RECESSI}/new`);

  /** Call api to remove recesso */
  removeRecesso = () => {
    RecessoService.deleteRecesso(this.state.currentRecesso.id)
      .then(() => {
        this.props.notification().addNotification({
          message: 'Eliminazione avvenuta con successo',
          level: 'success',
        });
        this.closeModalRemoveRecesso();
        this.loadData();
      })
      .catch((err) =>
        this.props.notification().addNotification({
          message:
            _.get(err, 'data.detail') || "Errore durante l'eliminazione, se il problema persiste contatta l'assistenza",
          level: 'error',
        })
      );
  };

  /** Close modal to remove recesso */
  closeModalRemoveRecesso = () => this.setState({ currentRecesso: null, removeRecessoConfirm: false });

  /** Close modal to update state */
  closeModalUpdateState = () => this.setState({ currentRecesso: null, updateState: false });

  render() {
    const { isLoading, recessi, page, totalPages, lastFilters, removeRecessoConfirm, currentRecesso, updateState } =
      this.state;
    const { notification, auth } = this.props;

    return (
      <div>
        <Helmet>
          <title>Gestione recessi</title>
          <meta name="description" content="Gestione recessi" />
        </Helmet>

        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>
            <Header as="h2" style={{ marginTop: 15 }}>
              <Icon name="users" />
              <Header.Content>
                Gestione recessi
                <Header.Subheader>Creazione e modifica recessi</Header.Subheader>
              </Header.Content>
            </Header>
          </span>
          <span>
            <div style={{ marginLeft: '0.5em', marginRight: '2.5em', background: '#EEEEEE', marginTop: 15 }}>
              <span style={{ paddingInline: '2.5em', color: 'lightslategrey', fontSize: '21px' }}>
                Totale recessi in lavorazione
              </span>
              <CounterLabel color="blue">{this.state.totalRecessi}</CounterLabel>
            </div>
          </span>
        </div>

        <TableOverflow>
          <Table
            isLoading={isLoading}
            columns={this.columns}
            canAction={{
              actions: <FilterBar auth={auth} addRecesso={this.addRecesso} onFilter={this.loadData} colspan={this.columns.length} />,
              active: true,
            }}
            canSort={{
              active: true,
              onSort: (field, dir) => {
                this.setState({ orderField: field, orderDirection: dir }, () => this.loadData(lastFilters));
              },
            }}
            elements={recessi}
            canPaginate={{
              active: true,
              render: (
                <Pagination
                  activePage={page}
                  boundaryRange={1}
                  onPageChange={(_e, { activePage }) =>
                    this.setState({ page: activePage }, () => this.loadData(lastFilters, false))
                  }
                  size="mini"
                  siblingRange={1}
                  totalPages={totalPages}
                  ellipsisItem={'...'}
                  lastItem={null}
                />
              ),
            }}
            emptyResults={<EmptyMessage>Nessun recesso disponibile</EmptyMessage>}
          />
        </TableOverflow>

        <Confirm
          size="tiny"
          content="Sei sicuro di voler eliminare il recesso?"
          open={removeRecessoConfirm}
          cancelButton="Annulla"
          confirmButton="Conferma"
          onCancel={this.closeModalRemoveRecesso}
          onConfirm={this.removeRecesso}
        />

        <Modal size="mini" closeIcon open={updateState} onClose={this.closeModalUpdateState}>
          <Header content="Aggiorna stato" />
          <UpdateStateRecesso
            closeModal={this.closeModalUpdateState}
            recesso={currentRecesso}
            notification={notification}
            loadData={this.loadData}
          />
        </Modal>
      </div>
    );
  }
}

RecessiList.propTypes = {
  /** Used for manage history of the browser -> redirect */
  history: PropTypes.object,
  /** Function for call the notification system */
  notification: PropTypes.func,
  /** Object with data of logged user */
  auth: PropTypes.object,
};

export default RecessiList;



// WEBPACK FOOTER //
// ./app/components/RecessoList/List/index.js